'use client';
import { Fragment, FC, useRef, useState } from 'react';
import {
  Transition,
  Dialog,
  TransitionChild,
  DialogPanel,
} from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import Link from 'next/link';

import NavigationLinks from '../Header/components/NavigationLinks';
import { Logo } from '@/components';
import {
  CallIcon,
  ChevronRightIcon,
  CloseIcon,
  HeartOutlineIcon,
  MessageIcon,
  UserIcon,
} from '@/icons';
import { Svg, Text } from '@/components';
import { CategoriesListProps } from '../Header/components/HoverDropdown';
import { COUNTRY_CODE, LANDLINE_NUMBER, MENU_LINKS } from '@/constants';
import { useUser } from '@/context/userContext';
import { usePathname, useRouter } from 'next/navigation';
import { useCleverTap } from '@/context/clevertapContext';
import WhatsappMenu from '@/components/Whatsapp/Menu';
import { useHeaderContext } from '@/context/headerContext';
import { storage } from '@/config';

type MenuDataTypes = {
  level: number;
  url_path: string;
  name: string;
  children: Array<CategoriesListProps>;
  prev: Array<MenuDataTypes>;
};

interface PropTypes {
  id: string;
  onClose: (...args: any) => void;
  show?: boolean;
  title?: string;
}

const Menu: FC<PropTypes> = ({ id, show = false, onClose }) => {
  const focusRef = useRef(null);
  const pathname = usePathname();
  const router: any = useRouter();
  const { pushEvent } = useCleverTap();
  const { hoverCategory, categoriesToShowOnHover: categories } =
    useHeaderContext();
  const {
    userDetails,
    setUserDetails,
    setShowLoginModal,
    setLoginRedirect,
    setLoginModalMessage,
    setLoginEventMetadata,
  } = useUser();

  const [menuData, setMenuData] = useState<MenuDataTypes>({
    level: 0,
    url_path: '',
    name: '',
    prev: [],
    children: [],
  });

  const defaultUrlPath =
    hoverCategory === 'shop'
      ? MENU_LINKS.SHOP
      : hoverCategory === 'gifting'
        ? '/gifting/all-gifts/'
        : '';

  const defaultName =
    hoverCategory === 'shop'
      ? 'Shop'
      : hoverCategory === 'gifting'
        ? 'Gifting'
        : '';

  const handleMenuIngress = (element: MenuDataTypes) => {
    setMenuData({
      level: element?.level + 1,
      url_path:
        menuData?.url_path === MENU_LINKS.GIFTING
          ? `${menuData?.url_path}/all-gifts/${element?.url_path}`
          : `${menuData?.url_path}/${element?.url_path}`,
      name: element?.name,
      prev: [menuData, ...element.prev],
      children: element?.children,
    });
  };

  const handleMenuEgress = () => {
    if (menuData?.level == 1) {
      setMenuData({
        level: 0,
        url_path: defaultUrlPath,
        name: defaultName,
        prev: [],
        children: categories,
      });
    } else {
      setMenuData({
        level: menuData?.level - 1,
        url_path: menuData?.url_path?.substring(
          0,
          menuData?.url_path?.lastIndexOf('/')
        ),
        name: menuData?.prev[0]?.name,
        children: menuData?.prev[0]?.children,
        prev: menuData?.prev?.slice(1),
      });
    }
  };

  const handleCloseAndReset = () => {
    const url_paths = menuData?.url_path?.split('/');
    if (url_paths?.length > 2) {
      pushEvent({
        event: 'Category Viewed',
        data: {
          'Category Name': menuData?.name,
          'Category URL Key': url_paths[url_paths.length - 1],
          'Category Path': pathname,
        },
      });
    }
    onClose();
    setMenuData({
      level: 0,
      url_path: defaultUrlPath,
      name: defaultName,
      prev: [],
      children: categories,
    });
  };

  const onAccount = (href = '/account/') => {
    const token = storage.get('token');
    if (token) {
      router.push(href);
      return;
    } else {
      if (userDetails?.id) {
        setUserDetails(null);
      }
      setLoginModalMessage(
        href === '/account/'
          ? 'Login using your mobile number to embark on an unforgettable food journey'
          : 'Log in to save and savour your favourite food tales!'
      );
      setLoginEventMetadata({
        source:
          href === '/account/'
            ? 'Hamburger Menu Account Login'
            : 'Hamburger Menu Shelf Login',
      });
      setLoginRedirect(href);
      setShowLoginModal(true);
    }
  };

  const MiscellaneousItems = () => {
    return (
      <div className='mb-4 flex flex-col text-black text-opacity-60'>
        <button
          className='flex items-center gap-x-3 p-5'
          onClick={() => {
            onAccount(userDetails ? '/account/' : '/');
            onClose();
          }}
          title={userDetails ? 'Account' : 'Login'}>
          <Svg fill='none' className='opacity-60'>
            <UserIcon />
          </Svg>
          <p>{userDetails ? 'My Account' : 'Login'}</p>
        </button>
        <button
          className='flex items-center gap-x-3 p-5'
          onClick={() => {
            onAccount('/account/shelf/');
            onClose();
          }}
          title='Shelf'>
          <Svg fill='none' className='opacity-60' viewBox='0 0 16 16'>
            <HeartOutlineIcon />
          </Svg>
          <p>My Shelf</p>
        </button>
        <Link
          className='flex items-center gap-x-3 p-5'
          href='/faqs'
          onClick={onClose}>
          <Svg
            fill='none'
            viewBox='0 0 18 18'
            className='stroke-black stroke-[1.6] opacity-60'>
            <MessageIcon />
          </Svg>
          <p>FAQs</p>
        </Link>
        <div className='flex cursor-pointer items-center gap-x-3 p-5'>
          <Svg
            fill='none'
            viewBox='0 0 18 18'
            className='stroke-black stroke-[1.6] opacity-60'>
            <CallIcon />
          </Svg>
          <a href={`tel:${COUNTRY_CODE}${LANDLINE_NUMBER}`}>
            Call us on: {COUNTRY_CODE} {LANDLINE_NUMBER}
          </a>
        </div>
        <WhatsappMenu />
      </div>
    );
  };

  return (
    <>
      <Transition appear show={show} as={Fragment}>
        <Dialog
          as='div'
          className='relative z-30'
          onClose={onClose}
          initialFocus={focusRef}
          aria-label={id}
          aria-labelledby={id}
          aria-describedby={id}>
          <TransitionChild
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'>
            <div className='fixed inset-0 bg-gray-900/70' />
          </TransitionChild>
          <TransitionChild
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 -translate-x-full'
            enterTo='opacity-100 -translate-x-0'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-x-0'
            leaveTo='opacity-0 -translate-x-full'>
            <DialogPanel
              className={twMerge(
                `fixed inset-y-0 left-0 w-[80vw] transform-gpu scroll-pt-6 overflow-y-scroll bg-white text-left transition-all`
              )}>
              <div className='flex flex-col bg-[#EDEAE9] p-6'>
                <div className='flex items-center justify-between'>
                  <Logo anchorClasses='outline-hidden' />
                  <button
                    className='rounded-full'
                    onClick={handleCloseAndReset}>
                    <Svg fill='none'>
                      <CloseIcon />
                    </Svg>
                  </button>
                </div>
              </div>
              {menuData?.level === 0 && (
                <>
                  <div className='flex flex-col justify-center pb-5 md:block'>
                    <NavigationLinks
                      menuOpen={show}
                      onMenuClose={onClose}
                      menuData={menuData}
                      setMenuData={setMenuData}
                    />
                  </div>
                  <div className='mx-4 border-t border-black/20' />
                  <MiscellaneousItems />
                </>
              )}
              {menuData?.level >= 1 && (
                <div className='flex h-full w-full flex-col gap-y-6 px-4'>
                  <div
                    onClick={handleMenuEgress}
                    className='group mt-6 flex items-center justify-start gap-2'>
                    <Svg
                      className='h-4 w-4 rotate-180 fill-none stroke-fig delay-300 ease-in'
                      viewBox='0 0 24 24'>
                      <ChevronRightIcon />
                    </Svg>
                    <div className='font-gilroy cursor-pointer text-lg font-bold text-fig underline'>
                      {menuData?.prev[0]?.name || 'All'}
                    </div>
                  </div>
                  <div className='flex items-start justify-between space-x-4'>
                    <Text className='px-0 text-xl font-semibold text-[#38383A]'>
                      {menuData?.name}
                    </Text>
                    <Link
                      href={menuData?.url_path}
                      onClick={handleCloseAndReset}
                      className='font-gilroy shrink-0 cursor-pointer text-base text-fig underline'>
                      View All
                    </Link>
                  </div>
                  {menuData?.children?.map(el => {
                    if (Number(el?.children_count) === 0) {
                      return (
                        <Link
                          href={menuData?.url_path + '/' + el?.url_key}
                          onClick={handleCloseAndReset}
                          className='group flex items-center justify-between'
                          key={el?.id}>
                          <div className='font-gilroy cursor-pointer text-lg text-[#38383A]'>
                            {el?.name}
                          </div>
                        </Link>
                      );
                    }
                    return (
                      <div
                        onClick={() => {
                          handleMenuIngress({
                            level: menuData?.level,
                            url_path: el?.url_key,
                            name: el.name,
                            prev: menuData?.prev,
                            children: el?.children,
                          });
                        }}
                        className='group flex items-center justify-between'
                        key={el?.id}>
                        <div className='font-gilroy cursor-pointer text-lg text-[#38383A]'>
                          {el?.name}
                        </div>
                        <Svg
                          className='h-4 w-4 fill-none stroke-fig delay-300 ease-in'
                          viewBox='0 0 24 24'>
                          <ChevronRightIcon />
                        </Svg>
                      </div>
                    );
                  })}
                </div>
              )}
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
    </>
  );
};

export default Menu;
